import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as pdfjsLib from 'pdfjs-dist';
import {
    Box,
    Typography,
    Button,
    Paper,
    Stack,
    LinearProgress,
    useTheme,
    Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.mjs`;

const PdfToImageUpload = ({ setValue }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [pageCount, setPageCount] = useState(0);

    const fileInputRef = useRef(null);
    const theme = useTheme();

    const generatePreview = useCallback(async (file) => {
        try {
            if (file.type === 'application/pdf') {
                const arrayBuffer = await file.arrayBuffer();
                const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
                setPageCount(pdf.numPages);

                // Generate preview of first page
                const page = await pdf.getPage(1);
                const viewport = page.getViewport({ scale: 0.5 });

                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                await page.render({ canvasContext: context, viewport }).promise;
                setPreview(canvas.toDataURL('image/jpeg', 0.7));
            } else if (file.type.startsWith('image/')) {
                setPageCount(1);
                const reader = new FileReader();
                reader.onload = (e) => setPreview(e.target.result);
                reader.readAsDataURL(file);
            }
        } catch (err) {
            console.error('Error generating preview:', err);
        }
    }, []);

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files?.[0];
        if (file) {
            await handleFile(file);
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (file) {
            await handleFile(file);
        }
    };

    const handleFile = async (file) => {
        if (file.type !== 'application/pdf' && !file.type.startsWith('image/')) {
            setError('Unsupported file type. Please upload a PDF or image.');
            return;
        }

        setLoading(true);
        setError(null);
        setSelectedFile(file);

        await generatePreview(file);

        // Pass the original file to parent
        setValue(file);

        setLoading(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setSelectedFile(null);
        setPreview(null);
        setPageCount(0);
        setError(null);
        setValue(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    return (
        <Box>
            {!selectedFile ? (
                <Paper
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
                        p: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        transition: 'border-color 0.2s',
                        '&:hover': {
                            borderColor: 'primary.main',
                        },
                    }}
                >
                    <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body1" gutterBottom>
                        {t('input_drag_drop')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Supports PDF and image files
                    </Typography>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,image/*"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                </Paper>
            ) : (
                <Paper
                    sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'success.main',
                        borderRadius: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        {/* Preview thumbnail */}
                        {preview && (
                            <Box
                                sx={{
                                    width: 80,
                                    height: 100,
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    flexShrink: 0,
                                    bgcolor: 'grey.200',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={preview}
                                    alt="Preview"
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                            </Box>
                        )}

                        {/* File info */}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <CheckCircleIcon color="success" fontSize="small" />
                                <Typography variant="body2" fontWeight={500} noWrap>
                                    {selectedFile.name}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                <Chip
                                    size="small"
                                    icon={selectedFile.type === 'application/pdf' ? <DescriptionIcon /> : <ImageIcon />}
                                    label={selectedFile.type === 'application/pdf' ? 'PDF' : 'Image'}
                                    variant="outlined"
                                />
                                <Chip
                                    size="small"
                                    label={formatFileSize(selectedFile.size)}
                                    variant="outlined"
                                />
                                {pageCount > 1 && (
                                    <Chip
                                        size="small"
                                        label={`${pageCount} pages`}
                                        variant="outlined"
                                    />
                                )}
                            </Stack>
                            <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                onClick={handleClear}
                            >
                                {t('input_drag_frop_clear')}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            )}

            {loading && <LinearProgress sx={{ mt: 2 }} />}
            {error && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default PdfToImageUpload;
